// src/PerformanceTemplate.js

import React, { useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const PerformanceTemplate = () => {
  const sectionNames = [
    'Report Information',
    'Operational Parameters',
    'Date and Time',
    'ECA',
  ];

  // State: one fields-array per section
  const [sections, setSections] = useState(
    sectionNames.map(name => ({
      name,
      fields: [
        {
          fieldName: '',
          type: 'String',
          validationRule: '',
          mandatory: false,
          dropdownList: '',
        },
      ],
      message: '',
    }))
  );

  // Add a blank field to section i
  const addField = (i) => {
    const updated = [...sections];
    updated[i].fields.push({
      fieldName: '',
      type: 'String',
      validationRule: '',
      mandatory: false,
      dropdownList: '',
    });
    setSections(updated);
  };

  // Remove field j from section i
  const removeField = (i, j) => {
    const updated = [...sections];
    updated[i].fields = updated[i].fields.filter((_, idx) => idx !== j);
    setSections(updated);
  };

  // Update a property of one field
  const handleFieldChange = (i, j, key, value) => {
    const updated = [...sections];
    updated[i].fields[j][key] = value;
    setSections(updated);
  };

  // POST just section i
  const saveSection = async (i) => {
    const { name, fields } = sections[i];
    const key = name.toLowerCase().replace(/\s+/g, '-'); // e.g. "Report Information" → "report-information"

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/templates/${key}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ templateName: name, fields }),
      });

      const updated = [...sections];
      updated[i].message = res.ok
        ? `${name} saved successfully!`
        : `Failed to save ${name}.`;
      setSections(updated);
    } catch (err) {
      console.error(err);
      const updated = [...sections];
      updated[i].message = `Error saving ${name}.`;
      setSections(updated);
    }
  };

  return (
    <div className="container my-4">
      <h2>Performance Template</h2>
      <p>Configure each section’s fields for your Performance Report.</p>

      <div className="accordion" id="perfAccordion">
        {sections.map((sec, i) => (
          <div className="accordion-item" key={i}>
            <h2 className="accordion-header" id={`head${i}`}>
              <button
                className="accordion-button collapsed"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target={`#sect${i}`}
                aria-expanded="false"
                aria-controls={`sect${i}`}
              >
                {sec.name}
              </button>
            </h2>

            <div
              id={`sect${i}`}
              className="accordion-collapse collapse"
              aria-labelledby={`head${i}`}
              data-bs-parent="#perfAccordion"
            >
              <div className="accordion-body">

                {/* Fields Table */}
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Field Name</th>
                      <th>Type</th>
                      <th>Validation Rule</th>
                      <th>Mandatory</th>
                      <th>Dropdown Options</th>
                      <th style={{ width: '1%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.fields.map((f, j) => (
                      <tr key={j}>
                        <td>
                          <input
                            className="form-control"
                            type="text"
                            value={f.fieldName}
                            onChange={e =>
                              handleFieldChange(i, j, 'fieldName', e.target.value)
                            }
                            placeholder="Enter field name"
                            required
                          />
                        </td>
                        <td>
                          <select
                            className="form-select"
                            value={f.type}
                            onChange={e =>
                              handleFieldChange(i, j, 'type', e.target.value)
                            }
                          >
                            {['String','Datetime','Number','Decimal','Dropdown']
                              .map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td>
                          <input
                            className="form-control"
                            type="text"
                            value={f.validationRule}
                            onChange={e =>
                              handleFieldChange(i, j, 'validationRule', e.target.value)
                            }
                            placeholder="e.g. /^[A-Z]{3}$/"
                          />
                        </td>
                        <td className="text-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={f.mandatory}
                            onChange={e =>
                              handleFieldChange(i, j, 'mandatory', e.target.checked)
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="form-control"
                            type="text"
                            value={f.dropdownList}
                            onChange={e =>
                              handleFieldChange(i, j, 'dropdownList', e.target.value)
                            }
                            placeholder="opt1,opt2,opt3"
                            disabled={f.type !== 'Dropdown'}
                          />
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            type="button"
                            onClick={() => removeField(i, j)}
                          >
                            &times;
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Section Actions */}
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    onClick={() => addField(i)}
                  >
                    + Add New Field
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={() => saveSection(i)}
                  >
                    Save {sec.name}
                  </button>
                </div>

                {/* Feedback */}
                {sec.message && (
                  <div className="mt-2 alert alert-info">
                    {sec.message}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PerformanceTemplate;
